import {useVoiceDictation} from 'app/pages/chatbot/components/hooks/useVoiceDictation'
import NativeVoiceListeningIndicator from 'app/pages/chatbot/components/voice/NativeVoiceListeningIndicator'
import VoiceMicButton from 'app/pages/chatbot/components/voice/VoiceMicButton'
import {Send, X} from 'lucide-react'
import React, {FC, useEffect, useId, useRef, useState} from 'react'
import {useIntl} from 'react-intl'

import type {VersiComposerProps} from './VersiComposer'

/** How long a dictation error (e.g. microphone denied) stays visible — the chat's own timing. */
const VOICE_ERROR_VISIBLE_MS = 6000

/**
 * Appends dictated speech to what is already in the composer instead of replacing it.
 * @param current The composer's text when the transcript arrived.
 * @param spoken The server's transcript.
 * @returns The combined instruction, trimmed; empty when both are empty.
 */
export const appendDictation = (current: string, spoken: string): string => {
  const base = current.trim()
  const text = spoken.trim()
  if (!base) return text
  if (!text) return base
  return `${base} ${text}`
}

/**
 * Versi's composer with the Versi chat's voice note (onboarding QA 18 Oct, `ONBOARDING_QA_OCT18`):
 * the chat's mic button, its listening strip with the live level meter (also "Transcribing…" while
 * the server works), and its Stop button — the same `useVoiceDictation` and
 * `chatbot/voice/transcribe` endpoint as `ChatInput`.
 *
 * Stop puts the transcript in the editable composer; nothing is sent until the user presses Send.
 * Cancel drops the recording without text. The microphone is released when the composer unmounts
 * — Assign with AI is conditionally rendered, so leaving it (grid, back to methods, next step)
 * unmounts this and the hooks stop both captures (the recorder and the level meter).
 * With `flagLowConfidence`, a transcript the server marks low-confidence highlights the composer
 * and shows the chat's "check the transcript" hint until the user edits or sends.
 */
const VersiVoiceComposer: FC<VersiComposerProps> = ({
  value,
  onChange,
  onSend,
  placeholder,
  disabled,
  flagLowConfidence,
}) => {
  const intl = useIntl()
  const t = (id: string) =>
    intl.formatMessage({id: `ONBOARDING_WIZARD.TEACHER_ALLOCATION.AI.${id}`})
  const checkId = useId()
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  // Read at transcription time, so text typed while recording is kept and the speech appended.
  const valueRef = useRef(value)
  valueRef.current = value
  const [checkTranscript, setCheckTranscript] = useState(false)
  const [shownErrorKey, setShownErrorKey] = useState<string | null>(null)

  const dictation = useVoiceDictation({
    onTranscribed: (text, meta) => {
      const composed = appendDictation(valueRef.current, text)
      if (!composed) return
      // The server decides what "low" means; the composer only renders its verdict.
      setCheckTranscript(Boolean(flagLowConfidence) && meta?.lowConfidence === true)
      onChange(composed)
      // The Stop button is swapped for Send in the same commit — focus after paint.
      requestAnimationFrame(() => textareaRef.current?.focus())
    },
  })

  // Dictation failures show under the composer for a few seconds, as in the chat.
  useEffect(() => {
    if (!dictation.errorKey) {
      setShownErrorKey(null)
      return
    }
    setShownErrorKey(dictation.errorKey)
    const timer = window.setTimeout(() => setShownErrorKey(null), VOICE_ERROR_VISIBLE_MS)
    return () => window.clearTimeout(timer)
  }, [dictation.errorKey])

  // Grow with the text, up to a few lines.
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`
  }, [value])

  // Recording and transcribing are both "busy" for the composer; only recording holds the mic.
  const isDictating = dictation.isRecording || dictation.isTranscribing
  const showMicButton = dictation.isSupported && !isDictating
  // Send sits next to the mic once there is something to send, and stands in for it entirely
  // when recording is unavailable.
  const showSendButton = !isDictating && (!showMicButton || value.trim().length > 0)
  const canSend = !disabled && value.trim().length > 0 && !isDictating
  const showCheck = checkTranscript && value.trim().length > 0

  const send = () => {
    if (!canSend) return
    setCheckTranscript(false)
    onSend()
  }

  return (
    <div className={`ta-composer ta-composer--voice${showCheck ? ' ta-composer--check' : ''}`}>
      <NativeVoiceListeningIndicator
        isListening={dictation.isRecording}
        isTranscribing={dictation.isTranscribing}
      />
      <textarea
        ref={textareaRef}
        rows={1}
        value={value}
        placeholder={placeholder}
        aria-label={placeholder}
        aria-describedby={showCheck ? checkId : undefined}
        onChange={(e) => {
          setCheckTranscript(false)
          onChange(e.target.value)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            send()
          }
        }}
      />
      <div className='ta-composer__buttons'>
        {showMicButton && (
          <VoiceMicButton
            isListening={false}
            onClick={() => void dictation.start()}
            hasError={dictation.status === 'error'}
            className={showSendButton ? 'voice-mic-button--secondary' : ''}
          />
        )}
        {dictation.isRecording && (
          <>
            <button
              type='button'
              className='ta-composer__voice-cancel'
              aria-label={t('RECORDING_CANCEL')}
              title={t('RECORDING_CANCEL')}
              onClick={dictation.cancel}
            >
              <X size={18} aria-hidden />
            </button>
            <button
              type='button'
              className='voice-stop-button'
              aria-label={intl.formatMessage({id: 'CHAT.VOICE.STOP'})}
              title={intl.formatMessage({id: 'CHAT.VOICE.STOP'})}
              onClick={() => void dictation.stop()}
            >
              <i className='fas fa-stop' style={{fontSize: 18}} aria-hidden />
            </button>
          </>
        )}
        {showSendButton && (
          <button
            type='button'
            className='ta-composer__send'
            aria-label={t('SEND')}
            disabled={!canSend}
            onClick={send}
          >
            <Send size={20} aria-hidden />
          </button>
        )}
      </div>
      {shownErrorKey ? (
        <p className='ta-composer__error' role='alert'>
          {intl.formatMessage({id: shownErrorKey})}
        </p>
      ) : (
        showCheck && (
          <p id={checkId} className='ta-composer__check' role='status'>
            {intl.formatMessage({id: 'CHAT.VOICE.CHECK_TRANSCRIPT'})}
          </p>
        )
      )}
    </div>
  )
}

export default VersiVoiceComposer
